import React, { useState } from 'react';
import { API_URL, apiFetch } from '../config/api';

/**
 * Static Calibration Status & Interactive Guide Dashboard
 * Displays real-time sensor status (Gyro, Accel, Compass, Level, Radio, ESC, Frame) on the left
 * Displays a premium interactive step-by-step Flight Crew Guide & Safety Checklist on the right.
 */
function CalibrationPanel({
    drones = [],
    selectedDroneId = null,
    telemetry = {},
    onStartCalibration = null
}) {
    const [activeGuideTab, setActiveGuideTab] = useState('gyro');
    
    // State for autopilot frame geometry configuration
    const [frameClass, setFrameClass] = useState('1'); // Quadcopter default
    const [frameType, setFrameType] = useState('1'); // X layout default
    const [writingFrame, setWritingFrame] = useState(false);
    const [frameResultMsg, setFrameResultMsg] = useState('');

    const handleWriteFrame = async () => {
        if (armed) {
            setFrameResultMsg('❌ Safety Lock: Disarm drone before parameter write.');
            return;
        }
        setWritingFrame(true);
        setFrameResultMsg('');
        try {
            const res = await apiFetch(`${API_URL}/frame-config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    droneId: selectedDroneId,
                    frameClass: parseInt(frameClass),
                    frameType: parseInt(frameType)
                })
            });
            const data = await res.json();
            if (data.success) {
                setFrameResultMsg('✅ Parameters written! Reboot autopilot to remap motor outputs.');
            } else {
                setFrameResultMsg(`❌ Error: ${data.message}`);
            }
        } catch (err) {
            setFrameResultMsg(`❌ Network Error: ${err.message}`);
        } finally {
            setWritingFrame(false);
        }
    };

    if (!selectedDroneId) {
        return (
            <div style={{
                background: 'var(--bgSecondary)',
                border: '1px solid var(--borderPrimary)',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--textMuted)',
                fontSize: '12px',
                fontStyle: 'italic',
                padding: '20px',
                textAlign: 'center'
            }}>
                Select a drone to view sensor calibration status & guides
            </div>
        );
    }

    // Find the selected drone in database list to read DB calibrationStatus
    const drone = (drones || []).find(d => d && (d.id === selectedDroneId || d.droneId === selectedDroneId));
    const calibrationStatus = drone?.calibrationStatus || {};

    const armed = telemetry.armed || false;
    const connected = telemetry.connected || false;

    // Sensor definitions
    const sensors = [
        {
            key: 'gyro',
            name: 'Gyroscope',
            desc: 'Static zero-rate offsets',
            icon: '🎛️',
            status: calibrationStatus.gyro?.status || 'uncalibrated',
            lastCal: calibrationStatus.gyro?.lastCalibrated
        },
        {
            key: 'accel',
            name: 'Accelerometer',
            desc: 'Gravity vectors & scaling',
            icon: '📐',
            status: calibrationStatus.accel?.status || 'uncalibrated',
            lastCal: calibrationStatus.accel?.lastCalibrated
        },
        {
            key: 'compass',
            name: 'Magnetometer',
            desc: 'Hard/soft iron compass mapping',
            icon: '🧭',
            status: calibrationStatus.compass?.status || 'uncalibrated',
            lastCal: calibrationStatus.compass?.lastCalibrated
        },
        {
            key: 'level',
            name: 'Level Horizon',
            desc: 'Autopilot board alignment',
            icon: '⚖️',
            status: calibrationStatus.level?.status || 'uncalibrated',
            lastCal: calibrationStatus.level?.lastCalibrated
        },
        {
            key: 'rc',
            name: 'Radio Control',
            desc: 'Transmitter stick endpoints',
            icon: '🎮',
            status: calibrationStatus.rc?.status || 'uncalibrated',
            lastCal: calibrationStatus.rc?.lastCalibrated
        },
        {
            key: 'esc',
            name: 'ESC Calibration',
            desc: 'Motor throttle synchronization',
            icon: '⚡',
            status: calibrationStatus.esc?.status || 'uncalibrated',
            lastCal: calibrationStatus.esc?.lastCalibrated
        },
        {
            key: 'frame',
            name: 'Frame Geometry',
            desc: 'Autopilot layout & motor remap',
            icon: '🛸',
            status: calibrationStatus.frame?.status || 'uncalibrated',
            lastCal: calibrationStatus.frame?.lastCalibrated
        }
    ];

    // Detailed step-by-step instructions for each calibration type
    const guideContent = {
        gyro: {
            title: 'Gyroscope Zero-Rate Guide',
            icon: '🎛️',
            steps: [
                'Ensure the drone is placed on a completely flat, stationary surface.',
                'Do not move, bump, or touch the vehicle during calibration.',
                'Keep clear of high-wind areas or vibrating machinery.',
                'The procedure completes autonomously in approximately 5 seconds.'
            ],
            checklist: [
                { label: 'Drone is completely still', checked: true },
                { label: 'Surface is structurally stable', checked: true }
            ]
        },
        accel: {
            title: 'Accelerometer 3D Vector Guide',
            icon: '📐',
            steps: [
                'The autopilot will prompt you to place the drone in 6 successive orientations.',
                'Follow the live MAVLink logs in the wizard terminal.',
                'Orientations: Level, Left side, Right side, Nose up, Nose down, and Back side.',
                'Hold the vehicle completely still in each orientation until the beep confirms.'
            ],
            checklist: [
                { label: 'Secure hand-hold possible', checked: true },
                { label: 'Clear flat surfaces available nearby', checked: true }
            ]
        },
        compass: {
            title: 'Magnetometer (Compass) Guide',
            icon: '🧭',
            steps: [
                'Perform this test outdoors, away from large metal structures or power lines.',
                'When active, rotate the vehicle slowly 360° along all rotational axes (pitch, roll, yaw).',
                'Keep rotating until the live MAVLink logs report completion.',
                'Avoid carrying cell phones or keys near the vehicle during this test.'
            ],
            checklist: [
                { label: 'Tested outdoors / away from metal', checked: true },
                { label: 'No magnetic materials on operator', checked: true }
            ]
        },
        level: {
            title: 'Horizon board alignment Guide',
            icon: '⚖️',
            steps: [
                'Ensure the vehicle is leveled perfectly on a flat ground reference surface.',
                'The autopilot registers this angle as a level pitch/roll state.',
                'Any misalignment here will cause the drone to drift during flight.',
                'Autonomous execution takes less than 3 seconds.'
            ],
            checklist: [
                { label: 'Ground is level (use a bubble level)', checked: true },
                { label: 'Drone legs/frame are fully seated flat', checked: true }
            ]
        },
        rc: {
            title: 'Radio Transmitter Stick Guide',
            icon: '🎮',
            steps: [
                'Turn on your remote control transmitter before starting.',
                'Once active, deflect both control sticks fully to all four extreme corners.',
                'Toggle all mapped auxiliary switches and slider controls through their full range.',
                'Confirm stick deflection limits appear on the console log terminal.'
            ],
            checklist: [
                { label: 'RC transmitter turned ON', checked: true },
                { label: 'Sticks are clear to move freely', checked: true }
            ]
        },
        esc: {
            title: 'ESC Throttle Sync Guide',
            icon: '⚡',
            danger: true,
            steps: [
                '⚠️ CRITICAL: REMOVE ALL PROPELLERS FROM THE DRONE BEFORE PROCEEDING.',
                'Disconnect the drone battery, leaving MAVLink USB telemetry connected.',
                'Push the transmitter throttle stick to the maximum position, then boot the wizard.',
                'Plug in the drone battery. The motors will emit sync tones. Lower throttle fully.'
            ],
            checklist: [
                { label: '⚠️ ALL PROPELLERS REMOVED', checked: true, critical: true },
                { label: 'Battery initially disconnected', checked: true }
            ]
        },
        frame: {
            title: 'Autopilot Frame Configuration',
            icon: '🛸',
            steps: [
                'Select the physical frame class matching your multirotor (e.g. Quadcopter, Hexacopter).',
                'Select the flight geometry type (e.g. Plus, X, H) to match your motor placement.',
                'Click on "Write Frame Parameters" to save these configuration registers to the flight controller.',
                '⚠️ CRITICAL: You must completely reboot (power-cycle) the autopilot after writing frame geometry for the motor remapping to take effect.'
            ],
            checklist: [
                { label: 'Drone legs / arm attachments are secure', checked: true },
                { label: 'Motors match correct numbering sequence', checked: true },
                { label: 'Autopilot is disarmed', checked: true }
            ]
        }
    };

    const activeGuide = guideContent[activeGuideTab] || guideContent.gyro;

    const getStatusStyle = (status) => {
        const normalized = String(status || '').toLowerCase();
        if (normalized === 'calibrated' || normalized === 'success') {
            return {
                bg: 'color-mix(in srgb, var(--success) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--success) 40%, transparent)',
                color: 'var(--success)',
                label: '✓ CALIBRATED'
            };
        }
        if (normalized === 'failed') {
            return {
                bg: 'color-mix(in srgb, var(--danger) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--danger) 40%, transparent)',
                color: 'var(--danger)',
                label: '✗ FAILED'
            };
        }
        return {
            bg: 'color-mix(in srgb, var(--warning) 5%, transparent)',
            border: '1px solid color-mix(in srgb, var(--warning) 30%, transparent)',
            color: 'var(--warning)',
            label: '⚠ UNCALIBRATED'
        };
    };

    return (
        <div style={{
            background: 'var(--bgPrimary)',
            height: '100%',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Panel Header */}
            <div style={{
                marginBottom: '8px',
                paddingBottom: '6px',
                borderBottom: '1px solid var(--borderSecondary)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
            }}>
                <h3 style={{
                    margin: 0,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: 'var(--accent)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    🛡️ Sensor Calibration Dashboard
                </h3>
                {armed && (
                    <span style={{
                        fontSize: '9px',
                        color: 'var(--danger)',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
                        padding: '2px 6px',
                        border: '1px solid var(--danger)'
                    }}>
                        🔒 Flight Lock Active
                    </span>
                )}
            </div>

            {/* Split Grid Layout: Left (Status Rows) | Right (Interactive Manual & Guide) */}
            <div style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: '1.2fr 1fr',
                gap: '12px',
                minHeight: 0,
                overflow: 'hidden'
            }}>
                {/* Left Side: Sensor Status List */}
                <div style={{
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                    paddingRight: '2px',
                    height: '100%'
                }} className="custom-scrollbar">
                    {sensors.map((sensor) => {
                        const statusConfig = getStatusStyle(sensor.status);
                        const isClickable = connected && !armed && onStartCalibration;
                        const isSelectedTab = activeGuideTab === sensor.key;

                        return (
                            <div
                                key={sensor.key}
                                onClick={() => setActiveGuideTab(sensor.key)}
                                style={{
                                    background: isSelectedTab ? 'rgba(34, 211, 238, 0.05)' : 'var(--bgSecondary)',
                                    border: isSelectedTab ? '1px solid var(--accent)' : '1px solid var(--borderSecondary)',
                                    padding: '5px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '8px',
                                    transition: 'all 0.2s ease',
                                    opacity: connected ? 1 : 0.6,
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={e => {
                                    if (!isSelectedTab) e.currentTarget.style.borderColor = 'var(--accent)';
                                }}
                                onMouseLeave={e => {
                                    if (!isSelectedTab) e.currentTarget.style.borderColor = 'var(--borderSecondary)';
                                }}
                            >
                                {/* Left Info */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                                    <span style={{ fontSize: '16px', flexShrink: 0 }}>{sensor.icon}</span>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            color: 'var(--textPrimary)',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {sensor.name}
                                        </div>
                                        <div style={{
                                            fontSize: '9px',
                                            color: 'var(--textMuted)',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {sensor.desc}
                                        </div>
                                    </div>
                                </div>

                                {/* Right Controls */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                                    {/* Status Badge */}
                                    <span style={{
                                        fontSize: '8px',
                                        fontWeight: 800,
                                        padding: '3px 6px',
                                        borderRadius: '3px',
                                        background: statusConfig.bg,
                                        border: statusConfig.border,
                                        color: statusConfig.color,
                                        whiteSpace: 'nowrap',
                                        textAlign: 'center',
                                        minWidth: '85px',
                                        display: 'inline-block'
                                    }}>
                                        {statusConfig.label}
                                    </span>

                                    {/* Calibration Trigger Gear */}
                                    {sensor.key !== 'frame' ? (
                                        <button
                                            disabled={!isClickable}
                                            onClick={() => {
                                                setActiveGuideTab(sensor.key);
                                                if (onStartCalibration) onStartCalibration(sensor.key);
                                            }}
                                            title={
                                                armed 
                                                    ? "Cannot calibrate while drone is ARMED" 
                                                    : !connected 
                                                        ? "Drone is OFFLINE" 
                                                        : `Calibrate ${sensor.name}`
                                            }
                                            style={{
                                                padding: '4px 8px',
                                                background: isClickable ? 'rgba(34, 211, 238, 0.1)' : 'color-mix(in srgb, var(--textPrimary) 2%, transparent)',
                                                color: isClickable ? 'var(--accent)' : 'var(--textMuted)',
                                                border: `1px solid ${isClickable ? 'var(--accent)' : 'color-mix(in srgb, var(--textPrimary) 8%, transparent)'}`,
                                                borderRadius: '4px',
                                                fontSize: '9px',
                                                fontWeight: 700,
                                                cursor: isClickable ? 'pointer' : 'not-allowed',
                                                margin: 0,
                                                width: 'auto',
                                                transition: 'all 0.2s ease',
                                                textTransform: 'uppercase'
                                            }}
                                            onMouseEnter={e => {
                                                if (isClickable) {
                                                    e.currentTarget.style.background = 'var(--accent)';
                                                    e.currentTarget.style.color = 'var(--bgPrimary)';
                                                }
                                            }}
                                            onMouseLeave={e => {
                                                if (isClickable) {
                                                    e.currentTarget.style.background = 'rgba(34, 211, 238, 0.1)';
                                                    e.currentTarget.style.color = 'var(--accent)';
                                                }
                                            }}
                                        >
                                            ⚙
                                        </button>
                                    ) : (
                                        <button
                                            disabled={!connected}
                                            onClick={() => setActiveGuideTab('frame')}
                                            title={!connected ? "Drone is OFFLINE" : "Configure Multirotor Geometry"}
                                            style={{
                                                padding: '4px 8px',
                                                background: connected ? 'rgba(34, 211, 238, 0.1)' : 'color-mix(in srgb, var(--textPrimary) 2%, transparent)',
                                                color: connected ? 'var(--accent)' : 'var(--textMuted)',
                                                border: `1px solid ${connected ? 'var(--accent)' : 'color-mix(in srgb, var(--textPrimary) 8%, transparent)'}`,
                                                borderRadius: '4px',
                                                fontSize: '9px',
                                                fontWeight: 700,
                                                cursor: connected ? 'pointer' : 'not-allowed',
                                                margin: 0,
                                                width: 'auto',
                                                transition: 'all 0.2s ease',
                                                textTransform: 'uppercase'
                                            }}
                                            onMouseEnter={e => {
                                                if (connected) {
                                                    e.currentTarget.style.background = 'var(--accent)';
                                                    e.currentTarget.style.color = 'var(--bgPrimary)';
                                                }
                                            }}
                                            onMouseLeave={e => {
                                                if (connected) {
                                                    e.currentTarget.style.background = 'rgba(34, 211, 238, 0.1)';
                                                    e.currentTarget.style.color = 'var(--accent)';
                                                }
                                            }}
                                        >
                                            📝
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
 
                {/* Right Side: Interactive Avionics Help Guide Card */}
                <div style={{
                    background: activeGuide.danger ? 'color-mix(in srgb, var(--danger) 2%, transparent)' : 'rgba(34, 211, 238, 0.01)',
                    border: `1px solid ${activeGuide.danger ? 'color-mix(in srgb, var(--danger) 15%, transparent)' : 'rgba(34, 211, 238, 0.1)'}`,
                    borderRadius: '8px',
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    height: '100%'
                }}>
                    {/* Guide Header */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        borderBottom: `1px solid ${activeGuide.danger ? 'color-mix(in srgb, var(--danger) 15%, transparent)' : 'rgba(34, 211, 238, 0.1)'}`,
                        paddingBottom: '6px',
                        marginBottom: '8px',
                        flexShrink: 0
                    }}>
                        <span style={{ fontSize: '18px' }}>{activeGuide.icon}</span>
                        <div style={{ minWidth: 0 }}>
                            <div style={{
                                fontSize: '11px',
                                fontWeight: 800,
                                color: activeGuide.danger ? 'var(--danger)' : 'var(--textPrimary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                {activeGuide.title}
                            </div>
                            <span style={{ fontSize: '8px', color: 'var(--textMuted)', textTransform: 'uppercase' }}>
                                FLIGHT CREW MANUAL
                            </span>
                        </div>
                    </div>
 
                    {/* Step Content */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        minHeight: 0
                    }} className="custom-scrollbar">
                        {/* Procedure Steps OR Frame Config Form */}
                        {activeGuideTab === 'frame' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <span style={{ fontSize: '8px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>
                                    🛸 Select Autopilot Frame Configuration:
                                </span>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <label style={{ fontSize: '8.5px', color: 'var(--textSecondary)', fontWeight: 600 }}>FRAME CLASS (`FRAME_CLASS`):</label>
                                        <select
                                            value={frameClass}
                                            onChange={e => setFrameClass(e.target.value)}
                                            style={{
                                                background: 'var(--bgSecondary)',
                                                border: '1px solid var(--borderSecondary)',
                                                color: 'var(--textPrimary)',
                                                padding: '4px 6px',
                                                fontSize: '10px',
                                                fontFamily: 'inherit',
                                                borderRadius: '4px',
                                                outline: 'none',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <option value="1">Quadcopter (Class 1)</option>
                                            <option value="2">Hexacopter (Class 2)</option>
                                            <option value="3">Octacopter (Class 3)</option>
                                            <option value="4">OctaQuad (Class 4)</option>
                                            <option value="5">Y6 (Class 5)</option>
                                            <option value="7">Tricopter (Class 7)</option>
                                        </select>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <label style={{ fontSize: '8.5px', color: 'var(--textSecondary)', fontWeight: 600 }}>FRAME GEOMETRY TYPE (`FRAME_TYPE`):</label>
                                        <select
                                            value={frameType}
                                            onChange={e => setFrameType(e.target.value)}
                                            style={{
                                                background: 'var(--bgSecondary)',
                                                border: '1px solid var(--borderSecondary)',
                                                color: 'var(--textPrimary)',
                                                padding: '4px 6px',
                                                fontSize: '10px',
                                                fontFamily: 'inherit',
                                                borderRadius: '4px',
                                                outline: 'none',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <option value="0">Plus (+ Layout)</option>
                                            <option value="1">X Layout</option>
                                            <option value="2">V Layout</option>
                                            <option value="3">H Layout</option>
                                            <option value="4">V-Tail Layout</option>
                                        </select>
                                    </div>
                                </div>

                                <button
                                    onClick={handleWriteFrame}
                                    disabled={writingFrame || !connected || armed}
                                    style={{
                                        marginTop: '4px',
                                        padding: '6px 12px',
                                        background: writingFrame || !connected || armed ? 'color-mix(in srgb, var(--textPrimary) 5%, transparent)' : 'var(--accent)',
                                        color: writingFrame || !connected || armed ? 'var(--textMuted)' : 'var(--bgPrimary)',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '9.5px',
                                        fontWeight: 800,
                                        cursor: writingFrame || !connected || armed ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        transition: 'all 0.2s ease',
                                        width: '100%',
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    {writingFrame ? '⏳ Writing parameters...' : '💾 WRITE FRAME PARAMETERS'}
                                </button>

                                {frameResultMsg && (
                                    <div style={{
                                        fontSize: '8.5px',
                                        fontWeight: 700,
                                        marginTop: '2px',
                                        padding: '4px 6px',
                                        background: 'color-mix(in srgb, var(--textPrimary) 2%, transparent)',
                                        borderRadius: '4px',
                                        color: frameResultMsg.includes('❌') ? 'var(--danger)' : 'var(--success)',
                                        lineHeight: '1.2'
                                    }}>
                                        {frameResultMsg}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                <span style={{ fontSize: '8px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                                    📋 Calibration Procedure:
                                </span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    {activeGuide.steps.map((step, idx) => (
                                        <div key={idx} style={{ display: 'flex', gap: '6px', fontSize: '9.5px', lineHeight: '1.3', color: 'var(--textSecondary)' }}>
                                            <span style={{ color: activeGuide.danger ? 'var(--danger)' : 'var(--accent)', fontWeight: 'bold' }}>{idx + 1}.</span>
                                            <span>{step}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Safety Checklist */}
                        <div style={{
                            marginTop: 'auto',
                            paddingTop: '8px',
                            borderTop: `1px dashed ${activeGuide.danger ? 'color-mix(in srgb, var(--danger) 15%, transparent)' : 'color-mix(in srgb, var(--textPrimary) 5%, transparent)'}`,
                            flexShrink: 0
                        }}>
                            <span style={{ fontSize: '8px', fontWeight: 800, color: activeGuide.danger ? 'var(--danger)' : 'var(--success)', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                                🛡️ Required Safety Protocols:
                            </span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {activeGuide.checklist.map((item, idx) => (
                                    <div key={idx} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontSize: '9px',
                                        color: item.critical ? 'var(--danger)' : 'var(--textSecondary)',
                                        fontWeight: item.critical ? 700 : 500
                                    }}>
                                        <span style={{ color: item.critical ? 'var(--danger)' : 'var(--success)' }}>✓</span>
                                        <span>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default React.memo(CalibrationPanel);
